/**
 * Die Koch-Methode: Reihenfolge, Stufen, Aufgaben, Auswertung.
 * (06.10.2026, Morse-Modul Bahn 1 — Martins Wahl: Koch-Methode)
 *
 * Koch (1936): von Anfang an im vollen Zeichentempo hören, mit nur ZWEI
 * Zeichen beginnen und erst ein neues dazunehmen, wenn 90 Prozent stimmen.
 * So lernt das Ohr den Klang eines Zeichens, statt Punkte zu zählen.
 *
 * Die Reihenfolge ist die verbreitete von LCWO (Learn CW Online) — Zeichen,
 * die verschieden klingen, kommen früh zusammen; X ist das letzte.
 *
 * Rein: der Zufall kommt von außen (`zufall`), damit Tests feste Aufgaben
 * bekommen. Seite und Server rechnen dieselbe Auswertung.
 */
import { MORSE_TABELLE, MORSE_VORGABE } from "./morse.js";

/** Die Reihenfolge, in der die Zeichen dazukommen (41 Zeichen). */
export const KOCH_REIHENFOLGE: readonly string[] = [
  "K", "M", "U", "R", "E", "S", "N", "A", "P", "T",
  "L", "W", "I", ".", "J", "Z", "=", "F", "O", "Y",
  ",", "V", "G", "5", "/", "Q", "9", "2", "H", "3",
  "8", "B", "?", "4", "7", "C", "1", "D", "6", "0",
  "X",
];

/** Stufe 1 = K und M; jede Stufe nimmt ein Zeichen dazu. */
const KOCH_ERSTE_STUFE = 1;
export const KOCH_LETZTE_STUFE = KOCH_REIHENFOLGE.length - 1;
/** Ab diesem Anteil richtiger Zeichen geht es eine Stufe weiter. */
export const KOCH_SCHWELLE = 0.9;
/** So viele Zeichen müssen auf einer Stufe mindestens gehört sein, bevor sie entschieden wird. */
export const KOCH_MINDEST_ZEICHEN = 50;
/** Über so viele der letzten Aufgaben einer Stufe wird MINDESTENS gerechnet (`kochFenster`). */
export const KOCH_FENSTER_AUFGABEN = 4;
/**
 * So viele Zeichen soll das Fenster fassen (16.10.2026): so viel wie vier Bündel der Hörrunde (4 × 25). Haben die
 * letzten vier Aufgaben weniger (z. B. „Augen zu“, fünf Buchstaben je Aufgabe), zählt `kochFenster` zurück, bis es
 * reicht. Entschieden wird ab `KOCH_MINDEST_ZEICHEN`; die 90-%-Bedingungen laufen über das ganze Fenster.
 *
 * ⚠️ Prüfbefund 16.10.: mit nur 50 Zeichen und einer Prüfung nach JEDEM Bündel (alle fünf Zeichen) traf jemand mit
 *    gleichbleibend 80 % die 90 % in 58 % der Fälle zufällig innerhalb von 500 Zeichen (Hören: 8 %). 100 Zeichen
 *    bringen „Augen zu“ auf das Maß des Hörens (Test „Glück reicht nicht“ in tests/shared/morse-ziel.test.ts).
 */
export const KOCH_FENSTER_ZEICHEN = 100;
/** Höchstens so viele Aufgaben fasst das Fenster — alte Fehler sollen nicht ewig mitzählen (20 × 5 = 100). */
export const KOCH_FENSTER_HOECHSTENS = 20;

/** Die Stufe in ihre Grenzen gezogen; Unsinn wird Stufe 1. */
export function kochStufe(roh: unknown): number {
  return typeof roh === "number" && Number.isFinite(roh)
    ? Math.min(KOCH_LETZTE_STUFE, Math.max(KOCH_ERSTE_STUFE, Math.round(roh)))
    : KOCH_ERSTE_STUFE;
}

/** Die Zeichen einer Stufe (Stufe n: die ersten n + 1). */
export function kochZeichen(stufe: number): string[] {
  return KOCH_REIHENFOLGE.slice(0, kochStufe(stufe) + 1);
}

/**
 * Womit geübt wird (08.10.2026, Martin: „Falls ich alle üben möchte, wäre es cool, wenn ich alle nehmen könnte und nicht
 * nur das neu hinzugefügte.“): „meine“ = die Zeichen der eigenen Stufe (wie immer, Koch-Reihenfolge); „alle“ = alle Zeichen
 * des Kurses, gleichmäßig gemischt. Nur „meine“ zählt für den Aufstieg — in „alle“ wird die Güte je Zeichen weiter gebucht,
 * aber keine Aufgabe für die Stufe (`kochZaehltFuerStufe` rechnet nur, was der Server dafür annimmt, memory/morse-store.ts).
 */
export type Umfang = "meine" | "alle";
const UMFANG_VORGABE: Umfang = "meine";

/** Nur „alle“ ist „alle“; alles andere (auch Unsinn aus dem Speicher) bleibt bei der Vorgabe. */
export function umfangVon(roh: unknown): Umfang {
  return roh === "alle" ? "alle" : UMFANG_VORGABE;
}

/** Die Zeichen, aus denen geübt wird: die der Stufe — oder alle des Kurses (in der Reihenfolge des Kurses). */
export function kochUebungsZeichen(stufe: number, umfang: Umfang = UMFANG_VORGABE): string[] {
  return umfang === "alle" ? [...KOCH_REIHENFOLGE] : kochZeichen(stufe);
}

/** Das neueste Zeichen einer Stufe. */
export function kochNeuesZeichen(stufe: number): string {
  return KOCH_REIHENFOLGE[kochStufe(stufe)]!;
}

/** Die Zeichen, die auf dieser Stufe NEU sind — auf Stufe 1 die ersten beiden (K und M), danach je eins. */
export function kochNeueZeichen(stufe: number): string[] {
  const s = kochStufe(stufe);
  return s === KOCH_ERSTE_STUFE ? KOCH_REIHENFOLGE.slice(0, 2) : [KOCH_REIHENFOLGE[s]!];
}

/**
 * Eine Stufe selbst wählen (08.10.2026, Martin: „… wenn man zu schnell voranschreitet, dass man wieder ein Level
 * zurückschalten kann?“): eine ganze Zahl von 1 bis zur höchsten Stufe, die das Konto erreicht hat. `hoechste` ist
 * die Obergrenze — wer nie so weit kam, kann sie nicht überspringen (der Aufstieg bleibt an die 90-%-Regel gebunden).
 * Die Gründe sind Namen für eine 400 („stufe“: keine ganze Zahl oder unter 1; „stufe-zu-hoch“: noch nicht erreicht).
 */
export function kochStufeWaehlen(roh: unknown, hoechste: number): { ok: true; stufe: number } | { ok: false; grund: "stufe" | "stufe-zu-hoch" } {
  if (typeof roh !== "number" || !Number.isInteger(roh) || roh < KOCH_ERSTE_STUFE) return { ok: false, grund: "stufe" };
  if (roh > kochStufe(hoechste)) return { ok: false, grund: "stufe-zu-hoch" };
  return { ok: true, stufe: roh };
}

/**
 * Was sich ändert, wenn die Übungsstufe von `von` auf `bis` wechselt: abwärts fallen die zuletzt dazugekommenen
 * Zeichen heraus, aufwärts kommen sie (wieder) dazu. Reihenfolge wie im Kurs.
 */
export function kochStufenWechsel(von: number, bis: number): { fallenWeg: string[]; kommenDazu: string[] } {
  const a = kochStufe(von);
  const b = kochStufe(bis);
  return { fallenWeg: b < a ? KOCH_REIHENFOLGE.slice(b + 1, a + 1) : [], kommenDazu: b > a ? KOCH_REIHENFOLGE.slice(a + 1, b + 1) : [] };
}

/** Ab welcher Stufe ein Zeichen dabei ist — `null`, wenn es im Kurs nicht vorkommt (Ä, ß, CH, manche Satzzeichen). */
export function kochStufeVon(zeichen: string): number | null {
  const i = KOCH_REIHENFOLGE.indexOf(zeichen);
  return i < 0 ? null : Math.max(KOCH_ERSTE_STUFE, i);
}

/**
 * Anfänger-Tempo (06.10.2026, Martin: „So ist es mir noch viel zu schwer.“): bis zu dieser Stufe ist die VORGABE
 * langsamer — die Zeichen klingen mit 15 WpM, die Pausen sind auf wirksame 5 WpM gedehnt. Danach gilt die
 * Vorgabe des Plans (20/10). Nur die Vorgabe — wer selbst ein Tempo wählt, behält es (memory/morse-store.ts).
 * Die Koch-Regel (90 %, neues Zeichen mindestens 5-mal) bleibt davon unberührt.
 */
export const KOCH_ANFAENGER_BIS_STUFE = 2;
const KOCH_ANFAENGER_TEMPO = { tempo: 15, wirksam: 5 } as const;

/** Das vorgegebene Tempo einer Stufe (Zeichentempo und wirksames Tempo in WpM). */
export function kochTempoVorgabe(stufe: number): { tempo: number; wirksam: number } {
  return kochStufe(stufe) <= KOCH_ANFAENGER_BIS_STUFE
    ? { tempo: KOCH_ANFAENGER_TEMPO.tempo, wirksam: KOCH_ANFAENGER_TEMPO.wirksam }
    : { tempo: MORSE_VORGABE.tempo, wirksam: MORSE_VORGABE.wirksam };
}

/** Wie gut ein Zeichen bisher sitzt: 0 bis 1, `null` = noch nie gehört. */
export type Guete = Readonly<Record<string, number | null | undefined>>;

/**
 * Wörter für später: echte deutsche Wörter und Funk-Abkürzungen. Eine Stufe
 * bekommt nur die, deren Zeichen sie schon kennt (`kochWoerter`).
 */
const KOCH_WOERTER: readonly string[] = [
  "ES", "UM", "MUSS", "RUM", "SUMMER", "MEER", "SEE", "SEEN", "MANN", "NAME", "NASE", "SAME", "ARM", "RAM",
  "PAPA", "MAMA", "SPUR", "PASS", "TEE", "TAT", "TANTE", "STERN", "MARKT", "PARK", "KAPPE", "KERN", "TANK",
  "LAMPE", "WALD", "WELT", "WASSER", "WIND", "WIE", "WILL", "LIEB", "TISCH", "KIND", "MUT", "RUF", "TAG",
  "FUNK", "FREI", "OFT", "OPA", "OMA", "ROT", "TOR", "WORT", "ZEIT", "JA", "JAHR", "ZUG", "FEST",
  "TU", "UR", "RST", "TEST", "CQ", "DE", "QTH", "QRZ", "PSE", "TNX", "73", "OM", "YL", "WX",
  "BERG", "HAUS", "HUND", "BUCH", "BAUM", "VATER", "VIER", "FUENF", "ZWEI", "DREI", "ACHT", "SECHS",
];

/** Die Wörter, die sich auf dieser Stufe ganz aus bekannten Zeichen schreiben lassen. */
export function kochWoerter(stufe: number, liste: readonly string[] = KOCH_WOERTER): string[] {
  const bekannt = new Set(kochZeichen(stufe));
  return liste.filter((w) => [...w].every((z) => bekannt.has(z)));
}

/** Ab so vielen Wörtern bietet die Stufe Wortaufgaben an. */
export const KOCH_WOERTER_AB = 8;

export interface KochAufgabe {
  art: "gruppen" | "woerter";
  /** Was gegeben wird, Gruppen/Wörter durch ein Leerzeichen getrennt. */
  text: string;
}

/**
 * Eine Aufgabe für diese Stufe.
 *
 *   - `gruppen`: `anzahl` Gruppen zu je 5 Zeichen. Das neueste Zeichen kommt
 *     dreimal so oft wie ein gefestigtes, ein wackliges (Güte unter 0,9 oder
 *     nie gehört) doppelt so oft — Koch übt, was noch nicht sitzt.
 *   - `woerter`: `anzahl` Wörter aus `kochWoerter` (gibt es zu wenige, Gruppen).
 *
 * ⚠️ Das neue Zeichen kommt in JEDER Aufgabe vor (Prüfbefund 06.10.): nur
 *    solche Aufgaben zählen für den Aufstieg (`kochZaehltFuerStufe`). Bei den
 *    Wörtern ist jedes zweite eines mit dem neuen Zeichen; gibt es auf der
 *    Stufe kein solches Wort (bei 14 der Wort-Stufen, etwa „5“ oder „?“),
 *    kommen Gruppen. Fehlt es in den Gruppen zufällig ganz, ersetzt es ein
 *    Zeichen an zufälliger Stelle.
 *
 * `zufall` liefert Zahlen in [0, 1) — `Math.random` oder ein fester Zug im Test.
 */
export function kochAufgabe(
  stufe: number,
  o: { art?: KochAufgabe["art"]; anzahl?: number; guete?: Guete; zufall?: () => number; umfang?: Umfang } = {},
): KochAufgabe {
  const zufall = o.zufall ?? Math.random;
  const anzahl = Math.max(1, Math.min(20, Math.round(o.anzahl ?? 5)));
  const greif = <T>(liste: readonly T[]): T => liste[Math.min(liste.length - 1, Math.floor(zufall() * liste.length))]!;
  if (o.umfang === "alle") return kochAufgabeAlle(o.art ?? "gruppen", anzahl, greif);
  const neu = kochNeuesZeichen(stufe);
  if (o.art === "woerter") {
    const woerter = kochWoerter(stufe);
    const mitNeu = woerter.filter((w) => w.includes(neu));
    if (woerter.length >= KOCH_WOERTER_AB && mitNeu.length > 0) {
      return { art: "woerter", text: Array.from({ length: anzahl }, (_, i) => greif(i % 2 === 0 ? mitNeu : woerter)).join(" ") };
    }
  }
  const zeichen = kochZeichen(stufe);
  const gewichtet: string[] = [];
  for (const z of zeichen) {
    const g = o.guete?.[z];
    const gewicht = z === neu ? 3 : g === null || g === undefined || g < KOCH_SCHWELLE ? 2 : 1;
    for (let i = 0; i < gewicht; i++) gewichtet.push(z);
  }
  const gruppen = Array.from({ length: anzahl }, () => Array.from({ length: 5 }, () => greif(gewichtet)));
  if (!gruppen.some((g) => g.includes(neu))) {
    const g = gruppen[Math.min(gruppen.length - 1, Math.floor(zufall() * gruppen.length))]!;
    g[Math.min(4, Math.floor(zufall() * 5))] = neu;
  }
  return { art: "gruppen", text: gruppen.map((g) => g.join("")).join(" ") };
}

/**
 * Eine Aufgabe aus ALLEN Zeichen des Kurses (`umfang: "alle"`): jedes Zeichen gleich oft — kein neues Zeichen und kein
 * wackliges wird vorgezogen, auch ein noch nie gehörtes nicht (Martin will alle üben, nicht ein bestimmtes). Wörter
 * stehen auf jeder Stufe zur Verfügung (alle Buchstaben sind ja dabei), jedes gleich oft. Diese Aufgaben zählen nicht
 * für den Aufstieg: das neue Zeichen der Stufe muss hier nicht vorkommen.
 */
function kochAufgabeAlle(art: KochAufgabe["art"], anzahl: number, greif: <T>(liste: readonly T[]) => T): KochAufgabe {
  if (art === "woerter") {
    const woerter = kochWoerter(KOCH_LETZTE_STUFE);
    if (woerter.length >= KOCH_WOERTER_AB) return { art: "woerter", text: Array.from({ length: anzahl }, () => greif(woerter)).join(" ") };
  }
  const gruppen = Array.from({ length: anzahl }, () => Array.from({ length: 5 }, () => greif(KOCH_REIHENFOLGE)));
  return { art: "gruppen", text: gruppen.map((g) => g.join("")).join(" ") };
}

interface ZeichenErgebnis {
  zeichen: string;
  richtig: boolean;
  /** Was stattdessen kam (`null` = ausgelassen). */
  statt: string | null;
}

export interface Auswertung {
  /** Je gegebenem Zeichen, in der Reihenfolge der Aufgabe. */
  zeichen: ZeichenErgebnis[];
  richtig: number;
  /** Gegebene Zeichen. */
  gesamt: number;
  /** Zeichen, die getippt, aber nie gegeben wurden. */
  zuviel: number;
  /** richtig / (gesamt + zuviel) — wer rät und viel tippt, gewinnt nichts. */
  quote: number;
}

/** Nur Zeichen der Tabelle, in Großbuchstaben, ohne Leerraum. */
function nurZeichen(text: string): string[] {
  return [...text.normalize("NFC")]
    .map((z) => (z === "ß" ? z : z.toLocaleUpperCase("de-DE")))
    .filter((z) => MORSE_TABELLE[z] !== undefined);
}

/**
 * Was gegeben wurde (`soll`) gegen das, was getippt wurde (`ist`).
 *
 * Verglichen wird Zeichen für Zeichen über die kleinste Zahl von Änderungen
 * (Levenshtein), nicht Platz für Platz: wer ein Zeichen auslässt, hat EINEN
 * Fehler, nicht alle folgenden. Leerzeichen zählen nicht — Gruppen-Grenzen
 * hört man, aber sie sind kein Zeichen.
 */
export function kochAuswertung(soll: string, ist: string): Auswertung {
  const a = nurZeichen(soll);
  const b = nurZeichen(ist);
  const n = a.length;
  const m = b.length;
  // d[i][j] = Kosten von a[0..i) nach b[0..j)
  const d: number[][] = Array.from({ length: n + 1 }, (_, i) => Array.from({ length: m + 1 }, (_, j) => (i === 0 ? j : j === 0 ? i : 0)));
  for (let i = 1; i <= n; i++) {
    for (let j = 1; j <= m; j++) {
      d[i]![j] = Math.min(d[i - 1]![j]! + 1, d[i]![j - 1]! + 1, d[i - 1]![j - 1]! + (a[i - 1] === b[j - 1] ? 0 : 1));
    }
  }
  const ergebnis: ZeichenErgebnis[] = [];
  let zuviel = 0;
  let i = n;
  let j = m;
  while (i > 0 || j > 0) {
    if (i > 0 && j > 0 && d[i]![j] === d[i - 1]![j - 1]! + (a[i - 1] === b[j - 1] ? 0 : 1)) {
      ergebnis.push({ zeichen: a[i - 1]!, richtig: a[i - 1] === b[j - 1], statt: a[i - 1] === b[j - 1] ? null : b[j - 1]! });
      i--;
      j--;
    } else if (i > 0 && d[i]![j] === d[i - 1]![j]! + 1) {
      ergebnis.push({ zeichen: a[i - 1]!, richtig: false, statt: null });
      i--;
    } else {
      zuviel++;
      j--;
    }
  }
  ergebnis.reverse();
  const richtig = ergebnis.filter((e) => e.richtig).length;
  const nenner = n + zuviel;
  return { zeichen: ergebnis, richtig, gesamt: n, zuviel, quote: nenner === 0 ? 0 : richtig / nenner };
}

interface StufenEntscheid {
  stufe: number;
  aufgestiegen: boolean;
  /** Anteil richtig über das Fenster (`null`: noch zu wenig gehört). */
  quote: number | null;
  /** Anteil richtig beim NEUEN Zeichen über das Fenster (`null`: noch zu selten gehört). */
  neuQuote: number | null;
  /** So viele Zeichen lagen im Fenster. */
  zeichen: number;
}

/** So oft muss das neue Zeichen im Fenster gegeben worden sein, bevor die Stufe entschieden wird. */
export const KOCH_MINDEST_NEU = 5;

/** Eine Aufgabe, wie sie für die Stufe zählt: alle Zeichen, und das neue Zeichen für sich. */
export interface StufenAufgabe {
  richtig: number;
  gesamt: number;
  zuviel?: number;
  /** Wie oft das neue Zeichen der Stufe gegeben wurde, und wie oft es richtig war. */
  neuGesamt: number;
  neuRichtig: number;
}

/**
 * Zählt diese Aufgabe für die Stufe? Nur, wenn sie ausschließlich Zeichen der
 * Stufe enthält UND das neue Zeichen darin vorkommt. Gibt die Zahlen für
 * `kochStufeNach` zurück — oder `null`.
 *
 * ⚠️ Prüfbefund 06.10.: vorher zählte jede Aufgabe aus Zeichen der Stufe —
 *    wer sich Aufgaben nur aus K und M baute, stieg nach 45 Buchungen bis
 *    Stufe 23, ohne ein einziges neues Zeichen gehört zu haben.
 */
export function kochZaehltFuerStufe(stufe: number, a: Auswertung): StufenAufgabe | null {
  const bekannt = new Set(kochZeichen(stufe));
  const neu = kochNeuesZeichen(stufe);
  if (a.gesamt === 0 || !a.zeichen.every((z) => bekannt.has(z.zeichen))) return null;
  const neue = a.zeichen.filter((z) => z.zeichen === neu);
  if (neue.length === 0) return null;
  return { richtig: a.richtig, gesamt: a.gesamt, zuviel: a.zuviel, neuGesamt: neue.length, neuRichtig: neue.filter((z) => z.richtig).length };
}

/**
 * Das Fenster der Stufe: die letzten `KOCH_FENSTER_AUFGABEN` Aufgaben (neueste zuletzt) — und, solange sie zusammen
 * weniger als `KOCH_FENSTER_ZEICHEN` Zeichen haben, so viele ältere dazu, bis es reicht, höchstens
 * `KOCH_FENSTER_HOECHSTENS`. EINE Regel für Server (Buchung), Vespera (`morse_stand`) und Seite (`kochStufenZiel`).
 *
 * ⚠️ 16.10.2026, Martin: drei Tage „Augen zu“ geübt, kein Aufstieg. Dort ist eine Aufgabe fünf Buchstaben — fest vier
 *    Aufgaben waren höchstens 20 von 50 Zeichen, die Regel war nie erfüllbar. Haben die letzten vier genug Zeichen
 *    (vier Bündel der Hörrunde haben 4 × 25 = 100), ist das Fenster GENAU das alte; anders wird es nur, wo die letzten
 *    vier zusammen unter 100 Zeichen haben (tests/shared/morse-ziel.test.ts vergleicht gegen die alte Regel).
 */
export function kochFenster<T extends StufenAufgabe>(aufgaben: ReadonlyArray<T>): T[] {
  const ende = aufgaben.length;
  let n = Math.min(ende, KOCH_FENSTER_AUFGABEN);
  let zeichen = 0;
  for (let i = ende - n; i < ende; i++) zeichen += Math.max(0, aufgaben[i]!.gesamt);
  while (zeichen < KOCH_FENSTER_ZEICHEN && n < Math.min(ende, KOCH_FENSTER_HOECHSTENS)) {
    n++;
    zeichen += Math.max(0, aufgaben[ende - n]!.gesamt);
  }
  return aufgaben.slice(ende - n);
}

/**
 * Geht es eine Stufe weiter? Gerechnet über das Fenster DIESER Stufe (`kochFenster`: mindestens die letzten
 * `KOCH_FENSTER_AUFGABEN` Aufgaben, neueste zuletzt), die für sie zählen (`kochZaehltFuerStufe`). Zwei Bedingungen,
 * beide ab 90 Prozent:
 *   - alle Zeichen zusammen, ab `KOCH_MINDEST_ZEICHEN` gehörten Zeichen;
 *   - das NEUE Zeichen für sich, ab `KOCH_MINDEST_NEU` Mal gehört — sonst
 *     trügen die alten, sicheren Zeichen ein wackliges neues mit.
 *
 * ⚠️ Nur Aufgaben der aktuellen Stufe: wer auf Stufe 3 glänzte, ist auf
 *    Stufe 4 mit einem neuen Zeichen noch nicht geprüft.
 */
export function kochStufeNach(stufe: number, aufgaben: ReadonlyArray<StufenAufgabe>): StufenEntscheid {
  const s = kochStufe(stufe);
  const fenster = kochFenster(aufgaben);
  const summe = (f: (a: StufenAufgabe) => number | undefined): number => fenster.reduce((x, a) => x + Math.max(0, f(a) ?? 0), 0);
  const richtig = summe((a) => a.richtig);
  const gesamt = summe((a) => a.gesamt);
  const nenner = gesamt + summe((a) => a.zuviel);
  const neuGesamt = summe((a) => a.neuGesamt);
  const neuQuote = neuGesamt >= KOCH_MINDEST_NEU ? summe((a) => a.neuRichtig) / neuGesamt : null;
  if (gesamt < KOCH_MINDEST_ZEICHEN || nenner === 0) return { stufe: s, aufgestiegen: false, quote: null, neuQuote, zeichen: gesamt };
  const quote = richtig / nenner;
  const weiter = quote >= KOCH_SCHWELLE && neuQuote !== null && neuQuote >= KOCH_SCHWELLE && s < KOCH_LETZTE_STUFE;
  return { stufe: weiter ? s + 1 : s, aufgestiegen: weiter, quote, neuQuote, zeichen: gesamt };
}

/** Welche Bedingung des Aufstiegs gerade fehlt — in der Reihenfolge, in der man sie angeht. */
export type ZielLuecke = "zeichen" | "neu-anzahl" | "quote" | "neu-quote";

/** Das Ziel einer Stufe, offen gelegt: dieselben Zahlen wie `kochStufeNach`, dazu, was fehlt. (16.10.2026) */
export interface StufenZiel {
  stufe: number;
  /** Das Zeichen, das mit dem Aufstieg dazukommt (`null` auf der letzten Stufe). */
  danach: string | null;
  /** Das neue Zeichen DIESER Stufe — es muss für sich sitzen. */
  neu: string;
  /** Aufgaben im Fenster (`kochFenster`: mindestens 4 soweit vorhanden, höchstens `KOCH_FENSTER_HOECHSTENS`). */
  aufgaben: number;
  /** Je Bedingung: erfüllt? (EINE Quelle — `zielPunkte` und `fehlt` lesen hier.) */
  ok: Readonly<Record<ZielLuecke, boolean>>;
  /** Zeichen im Fenster und wie viele es braucht. */
  zeichen: number;
  mindestZeichen: number;
  /** Anteil richtig im Fenster (`null`: noch nichts im Fenster). Entschieden wird erst ab `mindestZeichen`. */
  quote: number | null;
  /** Wie oft das neue Zeichen im Fenster vorkam und wie oft es muss. */
  neuGesamt: number;
  mindestNeu: number;
  /** Anteil richtig beim neuen Zeichen (`null`: noch nicht im Fenster). Entschieden wird erst ab `mindestNeu`. */
  neuQuote: number | null;
  schwelle: number;
  /** Die erste Bedingung, die noch fehlt — `null`: alles erfüllt (der nächste gezählte Lauf hebt die Stufe). */
  fehlt: ZielLuecke | null;
  /** So viele der vier Bedingungen sind erfüllt. */
  erfuellt: number;
}

/**
 * Das Ziel der Stufe aus dem Fenster (`kochStufeNach` entscheidet, dies erklärt es). 16.10.2026, Martin: *„seit ca.
 * 200 Mal vier Buchstaben … die Anzeige ändert sich nicht“* — die Seite zeigte nur das Mittel der Güte, nicht die
 * Bedingungen des Aufstiegs. Die Reihenfolge der Lücken: erst genug hören, dann das neue Zeichen oft genug, dann
 * alle richtig, dann das neue richtig. `fehlt === null` heißt genau das, was `kochStufeNach` zum Aufstieg braucht.
 */
export function kochStufenZiel(stufe: number, fenster: ReadonlyArray<StufenAufgabe>): StufenZiel {
  const s = kochStufe(stufe);
  const f = kochFenster(fenster);
  const summe = (g: (a: StufenAufgabe) => number | undefined): number => f.reduce((x, a) => x + Math.max(0, g(a) ?? 0), 0);
  const zeichen = summe((a) => a.gesamt);
  const nenner = zeichen + summe((a) => a.zuviel);
  const quote = nenner > 0 ? summe((a) => a.richtig) / nenner : null;
  const neuGesamt = summe((a) => a.neuGesamt);
  const neuQuote = neuGesamt > 0 ? summe((a) => a.neuRichtig) / neuGesamt : null;
  const ok = {
    zeichen: zeichen >= KOCH_MINDEST_ZEICHEN,
    "neu-anzahl": neuGesamt >= KOCH_MINDEST_NEU,
    quote: zeichen >= KOCH_MINDEST_ZEICHEN && quote !== null && quote >= KOCH_SCHWELLE,
    "neu-quote": neuGesamt >= KOCH_MINDEST_NEU && neuQuote !== null && neuQuote >= KOCH_SCHWELLE,
  } satisfies Record<ZielLuecke, boolean>;
  const reihe: ZielLuecke[] = ["zeichen", "neu-anzahl", "quote", "neu-quote"];
  return {
    stufe: s,
    danach: s < KOCH_LETZTE_STUFE ? KOCH_REIHENFOLGE[s + 1]! : null,
    neu: kochNeuesZeichen(s),
    aufgaben: f.length,
    zeichen,
    mindestZeichen: KOCH_MINDEST_ZEICHEN,
    quote,
    neuGesamt,
    mindestNeu: KOCH_MINDEST_NEU,
    neuQuote,
    schwelle: KOCH_SCHWELLE,
    ok,
    fehlt: reihe.find((k) => !ok[k]) ?? null,
    erfuellt: reihe.filter((k) => ok[k]).length,
  };
}

/** So stark zählt die jüngste Antwort in der Güte eines Zeichens (gleitendes Mittel). */
const GUETE_GEWICHT = 0.15;

/** Die Güte eines Zeichens nach einer Antwort: gleitendes Mittel, der erste Treffer zählt ganz. */
export function neueGuete(bisher: number | null | undefined, richtig: boolean): number {
  const x = richtig ? 1 : 0;
  if (bisher === null || bisher === undefined || !Number.isFinite(bisher)) return x;
  return bisher * (1 - GUETE_GEWICHT) + x * GUETE_GEWICHT;
}
